import React from 'react';

export type CarouselSlide = {
  kind?: 'single' | 'gallery';
  image?: string;
  images?: string[];
  title?: string;
  text?: string;
};

type Props = {
  slides: CarouselSlide[];
  editable?: boolean;
  onChange?: (slides: CarouselSlide[]) => void;
  className?: string;
  showDots?: boolean;
  showArrows?: boolean;
  variant?: 'classic' | 'cards' | 'minimal';
  dotPosition?: 'top' | 'bottom' | 'overlay-top' | 'overlay-bottom';
  dotStyle?: 'dot' | 'pill' | 'bar';
  transition?: 'subtle' | 'fade' | 'zoom' | 'slide' | 'none';
};

const ArrowButton: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({ children, className = '', ...rest }) => (
  <button
    type="button"
    className={`inline-flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 shadow-sm hover:bg-purple-50 hover:text-purple-700 transition ${className}`}
    {...rest}
  >
    {children}
  </button>
);

const Dot: React.FC<{ active?: boolean; onClick?: () => void; styleType?: 'dot' | 'pill' | 'bar' }> = ({ active, onClick, styleType='dot' }) => {
  const base = styleType === 'dot'
    ? 'h-2.5 w-2.5 rounded-full'
    : styleType === 'pill'
      ? 'h-2.5 w-5 rounded-full'
      : 'h-1.5 w-6 rounded';
  const activeCls = styleType === 'bar' ? 'bg-purple-600' : 'bg-purple-600 shadow-sm';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${base} transition ${active ? activeCls : 'bg-gray-300 hover:bg-gray-400'}`}
      aria-pressed={!!active}
    />
  );
};

const Carousel: React.FC<Props> = ({ slides, editable = false, onChange, className, showDots = true, showArrows = true, variant='classic', dotPosition='top', dotStyle='dot', transition }) => {
  const [index, setIndex] = React.useState(0);
  const clampIndex = React.useCallback((i: number) => {
    const n = slides.length;
    if (n === 0) return 0;
    return (i + n) % n;
  }, [slides.length]);
  const prev = () => setIndex(i => clampIndex(i - 1));
  const next = () => setIndex(i => clampIndex(i + 1));
  const go = (i: number) => setIndex(clampIndex(i));

  const updateSlide = (i: number, patch: Partial<CarouselSlide>) => {
    if (!onChange) return;
    const nextSlides = slides.map((s, idx) => (idx === i ? { ...s, ...patch } : s));
    onChange(nextSlides);
  };
  const addSlide = () => {
    if (!onChange) return;
    const nextSlides = [...slides, { title: 'New card', text: 'Add a description…', image: '/uploads/sample.jpg' }];
    onChange(nextSlides);
    setIndex(nextSlides.length - 1);
  };
  const removeSlide = (i: number) => {
    if (!onChange) return;
    const nextSlides = slides.filter((_, idx) => idx !== i);
    onChange(nextSlides);
    setIndex(old => clampIndex(Math.min(old, nextSlides.length - 1)));
  };

  const cur = slides[index] || {};
  const kind = cur.kind || 'single';
  // Auto-rotate gallery when more images than visible tiles
  const [tick, setTick] = React.useState(0);
  React.useEffect(() => {
    const total = (cur.images || []).length;
    if (kind === 'gallery' && total > 6) {
      const id = setInterval(() => setTick(t => t + 1), 2500);
      return () => clearInterval(id);
    }
    return;
  }, [kind, (cur.images || []).length]);

  // Determine chosen transition: prop overrides slide-level, else default
  const chosenTransition = ((cur as any)?.transition as any) ?? (transition as any) ?? 'subtle';
  // Map transition to CSS classes
  const enterCls = chosenTransition === 'none' ? ''
    : chosenTransition === 'fade' ? 'crs-enter'
    : chosenTransition === 'zoom' ? 'crs-enter-zoom'
    : chosenTransition === 'slide' ? 'crs-enter'
    : 'crs-enter';
  const tileCls = chosenTransition === 'none' ? ''
    : chosenTransition === 'fade' ? 'crs-tile-fade'
    : chosenTransition === 'zoom' ? 'crs-tile-zoom'
    : chosenTransition === 'slide' ? 'crs-tile-slide'
    : 'crs-tile-fade';
  const singleImgCls = chosenTransition === 'none' ? '' : 'crs-ken';

  const wrapperCls = variant === 'minimal'
    ? 'overflow-hidden rounded-xl border border-gray-200 bg-white'
    : variant === 'cards'
      ? 'overflow-hidden rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-gray-50 shadow-sm'
      : 'overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm';

  const dotsPosCls = dotPosition === 'top'
    ? 'absolute inset-x-0 top-0 z-10 flex items-center justify-center gap-2 py-3'
    : dotPosition === 'bottom'
      ? 'absolute inset-x-0 bottom-0 z-10 flex items-center justify-center gap-2 py-3'
      : dotPosition === 'overlay-top'
        ? 'absolute inset-x-0 top-2 z-10 flex items-center justify-center gap-2'
        : 'absolute inset-x-0 bottom-2 z-10 flex items-center justify-center gap-2';

  return (
    <div className={className || ''}>
      {/* Scoped animations for Carousel only */}
      <style>{`
        @keyframes crs_fade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes crs_zoom { from { transform: scale(1.03); opacity: .2 } to { transform: scale(1); opacity: 1 } }
        @keyframes crs_slide { from { transform: translateY(6px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
        @keyframes crs_ken { 0% { transform: scale(1) translate(0,0) } 50% { transform: scale(1.06) translate(1%, -1%) } 100% { transform: scale(1.12) translate(-1%, 1%) } }
        .crs-enter { animation: crs_fade .35s ease-out both, crs_slide .55s ease-out both; }
        .crs-enter-zoom { animation: crs_fade .35s ease-out both, crs_zoom .7s ease-out both; }
        .crs-ken { animation: crs_ken 12s ease-in-out both; }
        /* Tile variants */
        @keyframes crs_tile_fade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes crs_tile_zoom { from { transform: scale(1.08); opacity: .0 } to { transform: scale(1); opacity: 1 } }
        @keyframes crs_tile_slide { from { transform: translateY(6px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
        .crs-tile-fade { animation: crs_tile_fade .45s ease-out both; }
        .crs-tile-zoom { animation: crs_tile_zoom .55s ease-out both; }
        .crs-tile-slide { animation: crs_tile_slide .55s ease-out both; }
      `}</style>
      <div className={`relative ${wrapperCls}`}>
        {showDots && (
          <div className={dotsPosCls}>
            {slides.map((_, i) => (
              <Dot key={i} active={i === index} onClick={() => go(i)} styleType={dotStyle} />
            ))}
          </div>
        )}

        <div className={`relative aspect-[16/9] ${variant==='minimal' ? 'bg-gray-50' : 'bg-gradient-to-br from-gray-50 to-gray-100'} ${enterCls}`}>
          {kind === 'gallery' ? (
            <div className="absolute inset-0 grid grid-cols-3 grid-rows-2 gap-1 p-1">
              {(() => {
                const imgs = (cur.images || []);
                const maxTiles = 6;
                const visible = imgs.length <= maxTiles
                  ? imgs
                  : Array.from({ length: maxTiles }, (_, i) => imgs[(i + tick) % imgs.length]);
                return visible.map((src, i) => (
                  <div key={`${src}-${i}`} className={`relative overflow-hidden rounded ${tileCls}`}>
                    <img src={src} alt={cur.title || ''} className="absolute inset-0 h-full w-full object-cover" />
                  </div>
                ));
              })()}
            </div>
          ) : (
            cur.image && (
              <img src={cur.image} alt={cur.title || ''} className={`absolute inset-0 h-full w-full object-cover ${singleImgCls}`} />
            )
          )}
          {variant !== 'minimal' && <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />}

          {showArrows && (
            <>
              <div className="absolute inset-y-0 left-3 flex items-center">
                <ArrowButton onClick={prev} aria-label="Previous slide">
                  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                </ArrowButton>
              </div>
              <div className="absolute inset-y-0 right-3 flex items-center">
                <ArrowButton onClick={next} aria-label="Next slide">
                  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                </ArrowButton>
              </div>
            </>
          )}

          <div className="absolute bottom-3 left-3 right-3">
            <div className={`${variant==='cards' ? 'max-w-2xl rounded-2xl bg-white/80 backdrop-blur p-5 shadow-lg' : 'max-w-xl rounded-xl bg-white/80 backdrop-blur p-4 shadow-md'}`}>
              {editable ? (
                <>
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <label className="text-xs text-gray-600">Card type</label>
                    <select className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs" value={kind} onChange={(e)=> {
                      const k = e.target.value as 'single'|'gallery';
                      if (k === 'single') updateSlide(index, { kind: 'single', image: (cur.images && cur.images[0]) || cur.image, images: undefined });
                      else updateSlide(index, { kind: 'gallery', images: cur.images || (cur.image ? [cur.image] : []) });
                    }}>
                      <option value="single">Single image</option>
                      <option value="gallery">Gallery</option>
                    </select>
                    {kind === 'gallery' ? (
                      <button type="button" className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs hover:bg-gray-50" onClick={() => {
                        const onPick = (ev: any) => {
                          try {
                            const arr: string[] = (ev?.detail?.urls) || [];
                            if (arr && arr.length) updateSlide(index, { kind: 'gallery', images: [...(cur.images||[]), ...arr] });
                          } catch {}
                        };
                        try { window.addEventListener('media-picked', onPick, { once: true } as any); } catch {}
                        try { const ev: any = new CustomEvent('open-media-picker', { detail: { multiple: true } }); window.dispatchEvent(ev); } catch {}
                      }}>
                        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                        Pick images
                      </button>
                    ) : (
                      <>
                        <input className="rounded-md border border-gray-200 px-2 py-1 text-xs flex-1" value={cur.image || ''} onChange={(e)=> updateSlide(index, { image: e.target.value })} placeholder="Image URL" />
                      </>
                    )}
                  </div>
                  <input
                    className="mb-2 w-full rounded-md border border-gray-200 px-2 py-1 text-sm font-semibold text-gray-900"
                    value={cur.title || ''}
                    onChange={(e)=> updateSlide(index, { title: e.target.value })}
                    placeholder="Card title"
                  />
                  <textarea
                    className="w-full rounded-md border border-gray-200 px-2 py-1 text-sm text-gray-700"
                    value={cur.text || ''}
                    onChange={(e)=> updateSlide(index, { text: e.target.value })}
                    placeholder="Short description"
                  />
                  {kind === 'gallery' && (
                    <div className="mt-2 grid grid-cols-6 gap-2">
                      {(cur.images || []).map((src, i) => (
                        <div key={i} className="relative group border border-gray-200 rounded overflow-hidden">
                          <img src={src} className="h-14 w-full object-cover" />
                          <button type="button" className="absolute top-1 right-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/90 border border-gray-200 text-gray-700 opacity-0 group-hover:opacity-100" onClick={()=> {
                            const copy = [...(cur.images||[])];
                            copy.splice(i,1);
                            updateSlide(index, { images: copy });
                          }}>
                            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18"/><path d="M6 6l12 12"/></svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <>
                  {cur.title && <div className="text-lg font-semibold text-gray-900">{cur.title}</div>}
                  {cur.text && <div className="mt-1 text-sm text-gray-700">{cur.text}</div>}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {editable && (
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="rounded-lg border border-gray-200 bg-white p-3">
            <div className="text-sm font-medium text-gray-900">Slides</div>
            <div className="mt-2 space-y-2 max-h-40 overflow-auto pr-2">
              {slides.map((s, i) => (
                <button key={i} type="button" onClick={() => go(i)}
                  className={`flex w-full items-center justify-between rounded-md border px-2 py-1 text-sm ${i===index ? 'border-purple-300 bg-purple-50 text-purple-800' : 'border-gray-200 hover:bg-gray-50'}`}>
                  <span className="truncate">{s.title || `Slide ${i+1}`}</span>
                  <span className="text-xs text-gray-500">{i+1}/{slides.length}</span>
                </button>
              ))}
            </div>
            <div className="mt-2 flex items-center gap-2">
              <button className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50" onClick={addSlide}>
                <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                Add slide
              </button>
              {slides.length > 0 && (
                <button className="inline-flex items-center gap-1 rounded-md border border-red-200 px-2 py-1 text-xs text-red-700 hover:bg-red-50" onClick={() => removeSlide(index)}>
                  <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
                  Remove current
                </button>
              )}
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-3">
            <div className="text-sm font-medium text-gray-900">Media</div>
            <div className="mt-2 space-y-2">
              <input
                className="w-full rounded-md border border-gray-200 px-2 py-1 text-sm"
                value={cur.image || ''}
                onChange={(e)=> updateSlide(index, { image: e.target.value })}
                placeholder="Image URL"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Carousel;
